import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import type { ChatParticipantOption } from "./useChatParticipantOptions";

type Props = {
  open: boolean;
  onClose: () => void;
  currentUserId: string;
  /** When false, show blockingMessage and disable participant select. */
  canSelectParticipant: boolean;
  participantOptions: ChatParticipantOption[];
  participantsLoading: boolean;
  /** Explains why the user cannot pick someone (org missing, no membership, etc.). */
  blockingMessage?: string;
  onSubmit: (title: string, otherUserId: string) => Promise<void>;
  submitting: boolean;
};

export const CreateChatDialog: FC<Props> = ({
  open,
  onClose,
  currentUserId,
  canSelectParticipant,
  participantOptions,
  participantsLoading,
  blockingMessage,
  onSubmit,
  submitting,
}) => {
  const [title, setTitle] = useState("");
  const [otherUserId, setOtherUserId] = useState("");

  const options = participantOptions.filter((o) => o.userId !== currentUserId);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setOtherUserId("");
    }
  }, [open]);

  const handleSubmit = async () => {
    const t = title.trim();
    if (!t || !otherUserId) return;
    await onSubmit(t, otherUserId);
  };

  const showParticipantField = canSelectParticipant && !blockingMessage;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New chat</DialogTitle>
      <DialogContent>
        {blockingMessage && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {blockingMessage}
          </Typography>
        )}
        <TextField
          autoFocus
          margin="dense"
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />
        {showParticipantField && (
          <FormControl fullWidth>
            <InputLabel id="chat-other-user-label">Other participant</InputLabel>
            <Select
              labelId="chat-other-user-label"
              id="chat-other-user"
              value={otherUserId}
              label="Other participant"
              onChange={(e) => setOtherUserId(e.target.value as string)}
              disabled={participantsLoading}
            >
              {options.map((o) => (
                <MenuItem key={o.userId} value={o.userId}>
                  {o.displayName}
                  {o.roleLabel ? ` (${o.roleLabel})` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            submitting ||
            !title.trim() ||
            !otherUserId ||
            !canSelectParticipant ||
            !!blockingMessage
          }
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
